import { useState, useEffect } from 'react';
import { Wrench, Eye, Calendar, DollarSign, User, Truck as TruckIcon } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

interface MaintenanceDeduction {
  id: string;
  log_number: string;
  maintenance_type: string;
  service_date: string;
  driver_payment_amount: number;
  driver_charged_amount: number;
  driver_remaining_amount: number;
  cost: number;
  company_cost_amount: number;
  description: string | null;
  driver_id: string;
  driver_first_name: string;
  driver_last_name: string;
  truck_unit_number: string | null;
  trailer_unit_number: string | null;
  unit_type: string;
  driver_payment_status: string;
}

interface DeductionsMaintenanceViewProps {
  onViewDetails: (logId: string) => void;
}

export function DeductionsMaintenanceView({ onViewDetails }: DeductionsMaintenanceViewProps) {
  const toast = useToast();
  const [deductions, setDeductions] = useState<MaintenanceDeduction[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    loadMaintenanceDeductions();
  }, [dateRange]);

  async function togglePaymentStatus(deductionId: string, currentStatus: string) {
    try {
      const newStatus = currentStatus === 'paid' ? 'unpaid' : 'paid';
      
      const { error } = await supabase
        .from('maintenance_logs')
        .update({ driver_payment_status: newStatus })
        .eq('id', deductionId);

      if (error) throw error;

      // Update local state
      setDeductions(prev => prev.map(d => 
        d.id === deductionId 
          ? { ...d, driver_payment_status: newStatus }
          : d
      ));

      toast.success(`Payment status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast.error('Failed to update payment status');
    }
  }

  async function loadMaintenanceDeductions() {
    try {
      setLoading(true);
      
      let query = supabase
        .from('maintenance_logs')
        .select(`
          id,
          log_number,
          maintenance_type,
          service_date,
          driver_payment_amount,
          driver_charged_amount,
          driver_remaining_amount,
          cost,
          company_cost_amount,
          description,
          driver_id,
          driver_payment_status,
          unit_type,
          drivers!inner (
            id,
            first_name,
            last_name
          ),
          trucks (
            unit_number
          ),
          trailers (
            unit_number
          )
        `)
        .eq('paid_by', 'driver')
        .not('driver_id', 'is', null)
        .gt('driver_payment_amount', 0);

      if (dateRange.start) {
        query = query.gte('service_date', dateRange.start);
      }

      if (dateRange.end) {
        query = query.lte('service_date', dateRange.end);
      }

      const { data, error } = await query;

      if (error) throw error;

      const formattedData = (data || []).map((log: any) => ({
        id: log.id,
        log_number: log.log_number,
        maintenance_type: log.maintenance_type,
        service_date: log.service_date,
        driver_payment_amount: log.driver_payment_amount,
        driver_charged_amount: log.driver_charged_amount || 0,
        driver_remaining_amount: log.driver_remaining_amount || log.driver_payment_amount,
        cost: log.cost,
        company_cost_amount: log.company_cost_amount,
        description: log.description,
        driver_id: log.driver_id,
        driver_first_name: log.drivers?.first_name || '',
        driver_last_name: log.drivers?.last_name || '',
        truck_unit_number: log.trucks?.unit_number || null,
        trailer_unit_number: log.trailers?.unit_number || null,
        unit_type: log.unit_type,
        driver_payment_status: log.driver_payment_status || 'unpaid',
      }));

      // Sort: unpaid first, then paid, then by service date descending
      formattedData.sort((a, b) => {
        // First sort by payment status (unpaid = 0, paid = 1)
        const statusA = a.driver_payment_status === 'unpaid' ? 0 : 1;
        const statusB = b.driver_payment_status === 'unpaid' ? 0 : 1;
        if (statusA !== statusB) return statusA - statusB;
        
        // Then sort by service date descending
        return new Date(b.service_date).getTime() - new Date(a.service_date).getTime();
      });

      setDeductions(formattedData);
    } catch (error) {
      console.error('Error loading maintenance deductions:', error);
      toast.error('Failed to load maintenance deductions');
    } finally {
      setLoading(false);
    }
  }

  // Filter deductions based on search term (searches across all records)
  const filteredDeductions = deductions.filter(deduction => {
    const searchLower = searchTerm.toLowerCase();
    return (
      deduction.log_number.toLowerCase().includes(searchLower) ||
      deduction.driver_first_name.toLowerCase().includes(searchLower) ||
      deduction.driver_last_name.toLowerCase().includes(searchLower) ||
      `${deduction.driver_first_name} ${deduction.driver_last_name}`.toLowerCase().includes(searchLower) ||
      deduction.maintenance_type.toLowerCase().includes(searchLower) ||
      deduction.truck_unit_number?.toLowerCase().includes(searchLower) ||
      deduction.trailer_unit_number?.toLowerCase().includes(searchLower)
    );
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredDeductions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedDeductions = filteredDeductions.slice(startIndex, endIndex);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const stats = {
    total: filteredDeductions.length,
    totalAmount: filteredDeductions.reduce((sum, d) => sum + Number(d.driver_payment_amount || 0), 0),
    thisMonth: filteredDeductions.filter(d => {
      const deductionDate = new Date(d.service_date);
      const now = new Date();
      return deductionDate.getMonth() === now.getMonth() && deductionDate.getFullYear() === now.getFullYear();
    }).length,
    thisMonthAmount: filteredDeductions
      .filter(d => {
        const deductionDate = new Date(d.service_date);
        const now = new Date();
        return deductionDate.getMonth() === now.getMonth() && deductionDate.getFullYear() === now.getFullYear();
      })
      .reduce((sum, d) => sum + Number(d.driver_payment_amount || 0), 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Maintenance Deductions</h1>
        <p className="text-sm text-slate-600 mt-1">Driver payments for maintenance services</p>
      </div>

      {/* Stats Cards */}
      <div className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Wrench className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Deductions</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-2 rounded-lg">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Amount</p>
                <p className="text-2xl font-bold text-slate-900">${stats.totalAmount.toFixed(0)}</p>
              </div>
            </div>
          </div>

          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="bg-orange-100 p-2 rounded-lg">
                <Calendar className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">This Month</p>
                <p className="text-2xl font-bold text-slate-900">{stats.thisMonth}</p>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-2 rounded-lg">
                <DollarSign className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">This Month Amount</p>
                <p className="text-2xl font-bold text-slate-900">${stats.thisMonthAmount.toFixed(0)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by driver, log number, or unit..."
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="mt-4">
          <button
            onClick={() => {
              setSearchTerm('');
              setDateRange({ start: '', end: '' });
            }}
            className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                  Log Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                  Driver
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                  Unit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                  Maintenance Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                  Service Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                  Total Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                  Charged
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                  Remaining
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {paginatedDeductions.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-12 text-center text-slate-500">
                    <div className="flex flex-col items-center gap-3">
                      <Wrench className="w-12 h-12 text-slate-300" />
                      <p className="text-lg font-medium">No maintenance deductions found</p>
                      <p className="text-sm">Driver maintenance payments will appear here</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedDeductions.map((deduction) => (
                  <tr
                    key={deduction.id}
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => onViewDetails(deduction.id)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-semibold text-blue-600">{deduction.log_number}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        <span className="font-medium text-slate-900">
                          {deduction.driver_first_name} {deduction.driver_last_name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <TruckIcon className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-900">
                          {deduction.unit_type === 'truck' 
                            ? deduction.truck_unit_number 
                            : deduction.trailer_unit_number || '-'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-slate-900">{deduction.maintenance_type}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-900">
                          {new Date(deduction.service_date).toLocaleDateString()}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-semibold text-slate-900">
                        ${deduction.driver_payment_amount.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-semibold text-orange-600">
                        ${deduction.driver_charged_amount.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-semibold text-green-600">
                        ${deduction.driver_remaining_amount.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePaymentStatus(deduction.id, deduction.driver_payment_status);
                        }}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                          deduction.driver_payment_status === 'paid'
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                        }`}
                      >
                        {deduction.driver_payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewDetails(deduction.id);
                        }}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between px-4">
          <div className="text-sm text-slate-600">
            Showing {startIndex + 1} to {Math.min(endIndex, filteredDeductions.length)} of {filteredDeductions.length} results
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                // Show first page, last page, current page, and pages around current
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'border border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="px-2 text-slate-400">...</span>;
                }
                return null;
              })}
            </div>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Results Count */}
      {totalPages <= 1 && (
        <div className="mt-4 text-center text-sm text-slate-600">
          Showing {filteredDeductions.length} of {deductions.length} maintenance deductions
        </div>
      )}
    </div>
  );
}
